/**
 * Schritt 1 „Quelle" — kompakt, ein Knopfdruck bis zum Entwurf.
 *
 * 🔴 Matze 02.10.2026: „Es macht keinen Sinn, erst den Artikelstamm zu bilden und abzugleichen
 * und dann erst den Entwurf zu erstellen. Das muss alles auf einen Knopfdruck gehen … die
 * Quellseite muss viel übersichtlicher und kompakter erscheinen: Auswahl Artikel neu oder
 * Bestand erweitern, dann Hersteller, dann Katalogdateien, dann Zahlen, dann Entwurf
 * erstellen. Mehr Informationen brauchen wir hier nicht — unter Entwürfe werden alle weiteren
 * Entscheidungen getroffen."
 *
 * Deshalb hier nur noch:
 *   1. Was soll passieren?   2. Hersteller   3. Katalogdatei(en) des Herstellers
 *   4. Zahlen (aus der vorab gerechneten Vorschau, sofern vorhanden — keine Artikelliste)
 *   5. „Entwurf erstellen" — EIN Job (`POST /entwuerfe/erstellen`), Fortschritt im Knopf,
 *      danach öffnet die Seite den neuen Entwurf.
 *
 * Vorher rechnete das Tool zweimal: „Artikel bilden und abgleichen" und „Als Entwurf
 * speichern" lasen beide die Datei und prüften beide gegen den Bestand. Jetzt einmal.
 *
 * Seit 23.10.2026 (Matze: „die relevanten Listen entweder für 26 oder für 27 über einen
 * Button wählbar"): das Backend ordnet die Dateien des Hersteller-Ordners ein
 * (`GET /quellen/einordnung`) — Katalog mit Saison aus dem Namen, Begleitdatei (an der
 * Kopfzeile erkannt), fremd. Daraus ein Knopf je Saison, der die Katalogdatei wählt;
 * Begleitdateien sind nicht anhakbar (angehakt gälten sie als Katalog und fielen aus der
 * Begleitsuche — „keine Clubdeal-Liste im Ordner"), fremde Dateien bleiben wählbar, aber
 * mit Hinweis. Die pure Logik dazu steht in `lib/saisonWahl.ts`.
 *
 * Es wird nichts nach Plenty geschrieben. Alle Pfade OHNE `/api/`-Prefix.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilePlus2, RefreshCw, Sparkles } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import BetriebsartWahl, { betriebsartName, type Betriebsart } from '../components/BetriebsartWahl';
import Info from '../components/Info';
import LaufButton from '../components/LaufButton';
import MeldungsListe from '../components/MeldungsListe';
import { zaehle } from '../components/Vorsortierung';
import { megabyte, zahl, zeitpunkt } from '../lib/format';
import { dateienDerSaison, istWaehlbar, saisonDerAuswahl, saisonVorwahl } from '../lib/saisonWahl';
import type {
  ArtikelVorschauAntwort,
  DateiEinordnung,
  DriveOrdner,
  QuellenAntwort,
  QuellenEinordnung,
  StatusAntwort,
} from '../lib/typen';

const KASTEN = 'space-y-3 rounded border border-[#DAD9D9] bg-white p-4';

export default function QuellePage({ aufEntwurf }: {
  /** Nach dem Erstellen: in den neuen Entwurf springen. */
  aufEntwurf?: (entwurfId: number) => void;
} = {}) {
  const [status, setStatus] = useState<StatusAntwort | null>(null);
  const [quellen, setQuellen] = useState<QuellenAntwort | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  /** Vorbelegt ist nichts — die Frage soll bewusst beantwortet werden. */
  const [betriebsart, setBetriebsart] = useState<Betriebsart | null>(null);
  const [profil, setProfil] = useState<string>('');
  /** Mehrere Dateien sind erlaubt und bei JAKO Pflicht (TS + CTW + Neuheiten, über die EAN entdoppelt). */
  const [dateiIds, setDateiIds] = useState<string[]>([]);
  /** Hat der Anwender seit der Herstellerwahl selbst umgehakt? Dann wählt die Einordnung nicht mehr vor. */
  const [vonHand, setVonHand] = useState(false);

  /** Einordnung der Dateien des Herstellers: Katalog (Saison), Begleitdatei, fremd. */
  const [einordnung, setEinordnung] = useState<QuellenEinordnung | null>(null);
  const [holtEinordnung, setHoltEinordnung] = useState(false);
  /** Was das Backend gerade anliest („Datei 2 von 6 …") — alle paar Sekunden abgefragt. */
  const [einordnungStand, setEinordnungStand] = useState<string | null>(null);
  const [einordnungFehler, setEinordnungFehler] = useState<string | null>(null);

  /** Nur für die Zahlen: das vorab gerechnete Ergebnis (Nachtlauf oder letzter Entwurf). */
  const [vorab, setVorab] = useState<{ vorschau: ArtikelVorschauAntwort; gerechnetAm: string } | null>(null);
  const [holtVorab, setHoltVorab] = useState(false);

  const job = useJobStatus(null);
  const [startFehler, setStartFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const [statusAntwort, quellenAntwort] = await Promise.all([
        api.get<{ data: StatusAntwort }>('/programmes/artikelimport/status'),
        api.get<{ data: QuellenAntwort }>('/programmes/artikelimport/quellen'),
      ]);
      setStatus(statusAntwort.data.data);
      setQuellen(quellenAntwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? err?.message ?? 'Laden fehlgeschlagen.');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  /**
   * Katalogdatei vorwählen (Entscheidung Matze 25.09.2026): genau eine passende Datei → gewählt;
   * führt das Profil mehrere zusammen (JAKO), alle passenden; bei zwei ohne Zusammenführung
   * (adidas FW26 + SS26) entscheidet der Anwender.
   */
  const drive = quellen?.drive;
  useEffect(() => {
    const p = status?.profile.find(x => x.schluessel === profil);
    if (!p || !drive) return;
    setDateiIds(waehleDateienVor(drive, p.schluessel, p.mehrereDateien));
  }, [profil, drive, status]);

  const dateiUmschalten = useCallback((id: string) => {
    setVonHand(true);
    setDateiIds(alt => (alt.includes(id) ? alt.filter(d => d !== id) : [...alt, id]));
  }, []);

  // Einordnung je Hersteller holen — das erste Mal je Datei holt das Backend sie einmal
  // (Kopfzeile), danach aus dem Speicher. Solange steht „Dateien werden geprüft …".
  useEffect(() => {
    setEinordnung(null);
    setEinordnungStand(null);
    setEinordnungFehler(null);
    if (!profil) return;
    let abgebrochen = false;
    setHoltEinordnung(true);
    api.get<{ data: QuellenEinordnung }>('/programmes/artikelimport/quellen/einordnung', { params: { profil } })
      .then((antwort: { data: { data: QuellenEinordnung } }) => { if (!abgebrochen) setEinordnung(antwort.data.data); })
      .catch((err: any) => {
        if (!abgebrochen) {
          setEinordnungFehler(err?.response?.data?.error?.message ?? err?.message ?? 'Die Dateien ließen sich nicht einordnen.');
        }
      })
      .finally(() => { if (!abgebrochen) setHoltEinordnung(false); });
    // Solange gewartet wird: den Stand abfragen (eine neue Lieferung von 40 MB braucht ~30 s aus dem Drive).
    const zeiger = window.setInterval(() => {
      api.get<{ data: { text: string } | null }>('/programmes/artikelimport/quellen/einordnung', { params: { profil, stand: 1 } })
        .then((antwort: { data: { data: { text: string } | null } }) => { if (!abgebrochen) setEinordnungStand(antwort.data.data?.text ?? null); })
        .catch(() => undefined);
    }, 2500);
    return () => { abgebrochen = true; window.clearInterval(zeiger); };
  }, [profil]);

  const einordnungJeId = useMemo(
    () => new Map((einordnung?.dateien ?? []).map(d => [d.id, d] as const)),
    [einordnung],
  );
  const geaendertAm = useCallback(
    (id: string) => (quellen?.drive ?? []).flatMap(o => o.dateien).find(d => d.id === id)?.geaendertAm ?? null,
    [quellen],
  );

  /** Der Saison-Knopf: wählt die Katalogdatei(en) der Saison — nie Begleitdateien. */
  const saisonWaehlen = useCallback((saison: string) => {
    if (!einordnung) return;
    setVonHand(true);
    const p = status?.profile.find(x => x.schluessel === profil);
    setDateiIds(dateienDerSaison(einordnung.dateien, saison, p?.mehrereDateien === true, geaendertAm));
  }, [einordnung, geaendertAm, profil, status]);

  // Sobald die Einordnung da ist: die neueste Saison vorwählen — außer der Anwender hat
  // schon selbst gehakt. Eine angehakte Begleitdatei (aus der Vorauswahl) fliegt immer raus.
  useEffect(() => {
    if (!einordnung) return;
    setDateiIds(alt => alt.filter(id => istWaehlbar(einordnungJeId.get(id))));
    if (vonHand) return;
    const saison = saisonVorwahl(einordnung.saisons);
    if (!saison) return;
    const p = status?.profile.find(x => x.schluessel === profil);
    const vorwahl = dateienDerSaison(einordnung.dateien, saison, p?.mehrereDateien === true, geaendertAm);
    if (vorwahl.length > 0) setDateiIds(vorwahl);
    // Nur auf die neue Einordnung reagieren — nicht auf jedes Umhaken danach.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [einordnung]);

  // Zahlen aus der Vorab-Vorschau — sofort da, ohne Rechnen. Gilt nur für denselben Dateistand.
  useEffect(() => {
    setVorab(null);
    if (!profil || dateiIds.length === 0) return;
    let abgebrochen = false;
    setHoltVorab(true);
    api.get<{ data: { vorschau: ArtikelVorschauAntwort; gerechnetAm: string } | null }>(
      '/programmes/artikelimport/artikel-vorschau/vorab',
      { params: { profil, datei: dateiIds.join(',') } },
    ).then((antwort: { data: { data: { vorschau: ArtikelVorschauAntwort; gerechnetAm: string } | null } }) => {
      if (!abgebrochen) setVorab(antwort.data.data ?? null);
    })
      .catch(() => undefined)
      .finally(() => { if (!abgebrochen) setHoltVorab(false); });
    return () => { abgebrochen = true; };
  }, [profil, dateiIds]);

  // Fertig → in den neuen Entwurf.
  useEffect(() => {
    const id = Number(job.result?.entwurfId);
    if (job.status === 'completed' && Number.isFinite(id) && id > 0) aufEntwurf?.(id);
    // aufEntwurf bewusst nicht in den Abhängigkeiten — nur auf den Statuswechsel reagieren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.status, job.result]);

  const aktuellesProfil = status?.profile.find(p => p.schluessel === profil) ?? null;
  const flach = (t: string) => t.toLowerCase().replace(/[^a-z0-9]/g, '');
  const eigeneOrdner: DriveOrdner[] = (quellen?.drive ?? []).filter(o => profil && flach(o.name).includes(flach(profil)));
  const andereOrdner: DriveOrdner[] = (quellen?.drive ?? []).filter(o => !eigeneOrdner.includes(o));
  const dateiNamen = (quellen?.drive ?? []).flatMap(o => o.dateien).filter(d => dateiIds.includes(d.id)).map(d => d.name);

  const erstellen = async () => {
    if (!betriebsart || !profil || dateiIds.length === 0) return;
    setStartFehler(null);
    try {
      await job.startJob('/programmes/artikelimport/entwuerfe/erstellen', {
        profil,
        datei: dateiIds,
        betriebsart,
        name: `${betriebsartName(betriebsart)}: ${aktuellesProfil?.hersteller ?? profil} — ${dateiNamen.join(' + ') || 'Katalog'}`,
      });
    } catch (err: any) {
      setStartFehler(err?.response?.status === 409
        ? 'Es wird gerade schon ein Entwurf erstellt.'
        : (err?.response?.data?.error?.message ?? 'Start fehlgeschlagen.'));
    }
  };

  const bereit = Boolean(betriebsart && profil && dateiIds.length > 0);
  const jobFehler = job.status === 'failed' ? (job.job?.error ?? job.error ?? 'Entwurf erstellen fehlgeschlagen.') : null;

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Artikelimport</h1>
          <p className="mt-1 text-sm text-[#1A1A1A]/70">Quelle wählen, Entwurf erstellen — entschieden wird im Entwurf.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void laden()} disabled={laedt}>
          <RefreshCw className={`mr-2 h-4 w-4 ${laedt ? 'animate-spin' : ''}`} />
          {laedt ? 'Drive wird gelesen …' : 'Neu laden'}
        </Button>
      </header>

      {fehler && <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">{fehler}</div>}
      {quellen && <MeldungsListe meldungen={quellen.meldungen} />}

      {/* ── 1. Was soll passieren? ─────────────────────────────────────── */}
      <section className={KASTEN}>
        <h2 className="font-semibold text-[#1A1A1A]">1. Was soll passieren?</h2>
        <BetriebsartWahl wert={betriebsart} aufWahl={setBetriebsart} />
      </section>

      {/* ── 2. Hersteller ──────────────────────────────────────────────── */}
      <section className={`${KASTEN} ${betriebsart ? '' : 'opacity-50'}`}>
        <h2 className="font-semibold text-[#1A1A1A]">2. Hersteller</h2>
        <div className="flex flex-wrap gap-2">
          {(status?.profile ?? []).map(p => (
            <button
              key={p.schluessel}
              type="button"
              onClick={() => { setProfil(p.schluessel); setDateiIds([]); setVonHand(false); }}
              className={`rounded border px-3 py-1.5 text-sm ${
                profil === p.schluessel
                  ? 'border-[#1A1A1A] bg-[#E9FF57] text-[#1A1A1A]'
                  : 'border-[#DAD9D9] bg-white text-[#1A1A1A] hover:border-[#1A1A1A]'
              }`}
            >
              {p.hersteller}
            </button>
          ))}
          {(status?.profile ?? []).length === 0 && !laedt && (
            <p className="text-sm text-[#1A1A1A]/60">Noch kein Hersteller-Profil eingerichtet.</p>
          )}
        </div>
      </section>

      {/* ── 3. Katalogdatei(en) ────────────────────────────────────────── */}
      <section className={`${KASTEN} ${profil ? '' : 'opacity-50'}`}>
        <h2 className="font-semibold text-[#1A1A1A]">3. Katalogdatei{aktuellesProfil?.mehrereDateien ? 'en' : ''}</h2>
        {!profil && <p className="text-sm text-[#1A1A1A]/60">Erst den Hersteller wählen.</p>}
        {profil && (
          <>
            <SaisonWahl
              einordnung={einordnung}
              holt={holtEinordnung}
              stand={einordnungStand}
              aktiv={einordnung ? saisonDerAuswahl(einordnung.dateien, dateiIds) : null}
              aufWahl={saisonWaehlen}
            />
            {einordnungFehler && (
              <p className="text-sm text-red-800">Dateien nicht eingeordnet: {einordnungFehler} — die Liste ist trotzdem wählbar.</p>
            )}
            <DateiListe ordner={eigeneOrdner} gewaehlt={dateiIds} aufUmschalten={dateiUmschalten} einordnung={einordnungJeId} />
            {einordnung && einordnung.meldungen.length > 0 && (
              <ul className="space-y-0.5 text-xs text-[#1A1A1A]/60">
                {einordnung.meldungen.map(m => <li key={m}>{m}</li>)}
              </ul>
            )}
            {eigeneOrdner.length === 0 && (
              <p className="text-sm text-[#1A1A1A]/60">Im Drive gibt es keinen Ordner zu diesem Hersteller.</p>
            )}
            {andereOrdner.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-[#1A1A1A]/60">Datei aus einem anderen Ordner</summary>
                <div className="mt-2">
                  <DateiListe ordner={andereOrdner} gewaehlt={dateiIds} aufUmschalten={dateiUmschalten} mitOrdnername />
                </div>
              </details>
            )}
          </>
        )}
      </section>

      {/* ── 4. Zahlen + 5. Entwurf erstellen ──────────────────────────── */}
      <section className={`${KASTEN} ${bereit ? '' : 'opacity-50'}`}>
        <h2 className="flex items-center gap-1.5 font-semibold text-[#1A1A1A]">
          4. Was in der Datei steckt
          <Info titel="Woher die Zahlen kommen">
            <p>Aus der vorab gerechneten Vorschau (Nachtlauf oder letzter Entwurf derselben Datei).</p>
            <p>„Entwurf erstellen" rechnet alles frisch — die Zahlen im Entwurf sind die verbindlichen.</p>
          </Info>
        </h2>
        <Zahlen vorab={vorab} holt={holtVorab} bereit={bereit} />

        <div className="flex flex-wrap items-center gap-3 border-t border-[#DAD9D9] pt-3">
          <LaufButton
            laeuft={job.isRunning}
            laufText="Entwurf wird erstellt"
            schritt={job.progress?.message ?? null}
            prozent={job.progress?.percentage ?? null}
            dauerHinweis="bei uhlsport etwa 1–2 Minuten"
            onClick={() => void erstellen()}
            disabled={!bereit || job.isRunning}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
            icon={<FilePlus2 className="mr-2 h-4 w-4" aria-hidden />}
          >
            5. Entwurf erstellen
          </LaufButton>
          <span className="text-xs text-[#1A1A1A]/60">In Plenty passiert dabei nichts.</span>
        </div>
        {(startFehler || jobFehler) && <p className="text-sm text-red-800">{startFehler ?? jobFehler}</p>}
      </section>
    </div>
  );
}

/**
 * Ein Knopf je Saison, die unter den Katalogen des Herstellers vorkommt. Der Klick wählt
 * die Katalogdatei der Saison; leuchtet, wenn die Auswahl genau dieser Saison entspricht.
 */
function SaisonWahl({ einordnung, holt, stand, aktiv, aufWahl }: {
  einordnung: QuellenEinordnung | null;
  holt: boolean;
  /** Fortschritt vom Backend, solange gelesen wird. */
  stand: string | null;
  aktiv: string | null;
  aufWahl: (saison: string) => void;
}) {
  if (holt) {
    return (
      <p className="text-xs text-[#1A1A1A]/60">
        <RefreshCw className="mr-1 inline h-3 w-3 animate-spin" aria-hidden />
        {stand ?? 'Dateien werden geprüft …'}
        {stand && <span className="ml-1">— nur neue Lieferungen werden angelesen, danach geht es sofort.</span>}
      </p>
    );
  }
  if (!einordnung || einordnung.saisons.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-[#1A1A1A]/70">Saison:</span>
      {einordnung.saisons.map(saison => (
        <button
          key={saison}
          type="button"
          onClick={() => aufWahl(saison)}
          className={`rounded border px-3 py-1 text-sm ${
            aktiv === saison
              ? 'border-[#1A1A1A] bg-[#E9FF57] text-[#1A1A1A]'
              : 'border-[#DAD9D9] bg-white text-[#1A1A1A] hover:border-[#1A1A1A]'
          }`}
        >
          {saison}
        </button>
      ))}
      <span className="text-xs text-[#1A1A1A]/50">wählt die Katalogdatei der Saison — Stammdaten und Clubdeal-Liste findet das Tool selbst</span>
    </div>
  );
}

/** Reihenfolge in der Liste: Kataloge zuerst, Begleitdateien zuletzt (wie das Backend sie liefert). */
function sortiertNachEinordnung<T extends { id: string }>(dateien: T[], einordnung?: Map<string, DateiEinordnung>): T[] {
  if (!einordnung || einordnung.size === 0) return dateien;
  const rang = (d: T) => {
    const e = einordnung.get(d.id);
    if (!e) return 1;
    return e.art === 'katalog' ? 0 : e.art === 'ungeprueft' ? 1 : e.art === 'fremd' ? 2 : 3;
  };
  return [...dateien].sort((a, b) => rang(a) - rang(b));
}

/** Die Dateien der Ordner als kompakte Auswahl. */
function DateiListe({ ordner, gewaehlt, aufUmschalten, mitOrdnername = false, einordnung }: {
  ordner: DriveOrdner[];
  gewaehlt: string[];
  aufUmschalten: (id: string) => void;
  mitOrdnername?: boolean;
  /** Einordnung je Datei-Id — nur für die eigenen Ordner des Herstellers. */
  einordnung?: Map<string, DateiEinordnung>;
}) {
  return (
    <div className="space-y-1">
      {ordner.map(o => (
        <div key={o.id} className="space-y-1">
          {mitOrdnername && <p className="text-xs uppercase tracking-wide text-[#1A1A1A]/50">{o.name}</p>}
          {sortiertNachEinordnung(o.dateien.filter(d => d.lesbar), einordnung).slice(0, 8).map(datei => {
            const e = einordnung?.get(datei.id);
            const waehlbar = istWaehlbar(e);
            const ist = gewaehlt.includes(datei.id);
            return (
              <label
                key={datei.id}
                className={`flex items-center gap-3 rounded border px-3 py-1.5 text-sm ${
                  !waehlbar ? 'cursor-default border-dashed border-[#DAD9D9] text-[#1A1A1A]/60'
                    : ist ? 'cursor-pointer border-[#1A1A1A] bg-[#E9FF57]/40'
                      : e?.art === 'fremd' ? 'cursor-pointer border-[#DAD9D9] text-[#1A1A1A]/60'
                        : 'cursor-pointer border-[#DAD9D9]'
                }`}
              >
                {waehlbar
                  ? <input type="checkbox" checked={ist} onChange={() => aufUmschalten(datei.id)} />
                  : <Sparkles className="h-3.5 w-3.5 shrink-0 text-[#1A1A1A]/50" aria-label="wird automatisch gefunden" />}
                <span className="flex-1">
                  {datei.name}
                  {e?.art === 'katalog' && e.saison && (
                    <span className="ml-2 rounded bg-[#1A1A1A] px-1.5 py-0.5 text-[10px] font-semibold text-[#E9FF57]">{e.saison}</span>
                  )}
                  {e && e.art !== 'katalog' && e.grund && (
                    <span className="ml-2 text-xs text-[#1A1A1A]/50">{e.grund}</span>
                  )}
                </span>
                <span className="tabular-nums text-xs text-[#1A1A1A]/50">{megabyte(datei.groesseBytes)} · {zeitpunkt(datei.geaendertAm)}</span>
              </label>
            );
          })}
          {o.dateien.filter(d => d.lesbar).length === 0 && (
            <p className="text-sm text-[#1A1A1A]/50">keine Excel-Datei in „{o.name}"</p>
          )}
        </div>
      ))}
    </div>
  );
}

/** Eine Zeile Zahlen statt einer Artikelliste. */
function Zahlen({ vorab, holt, bereit }: {
  vorab: { vorschau: ArtikelVorschauAntwort; gerechnetAm: string } | null;
  holt: boolean;
  bereit: boolean;
}) {
  if (!bereit) return <p className="text-sm text-[#1A1A1A]/60">Erst Hersteller und Datei wählen.</p>;
  if (holt) return <p className="text-sm text-[#1A1A1A]/60">Zahlen werden geholt …</p>;
  if (!vorab) {
    return <p className="text-sm text-[#1A1A1A]/60">Für diese Datei liegen noch keine Zahlen vor — sie stehen nach dem Erstellen im Entwurf.</p>;
  }
  const v = vorab.vorschau;
  const z = zaehle(v.artikel.map(a => ({
    schluessel: a.schluessel,
    existenz: a.existenz.status,
    ampelKategorie: a.kategorien.ampel,
    ampelBild: a.bilder.ampel,
  })));
  const teile: Array<[string, number, string?]> = [
    ['Artikel', v.kennzahlen.artikel],
    ['Varianten', v.kennzahlen.varianten],
    ['neu mit Bildern', z.neu, 'text-green-800'],
    ['existieren schon', z.existiert],
    ['ohne Bild', z.ohneBild],
    ['ohne Kategorie', z.ohneKategorie],
  ];
  if (z.ungeprueft > 0) teile.push(['unklar', z.ungeprueft]);
  return (
    <div className="space-y-1">
      <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {teile.map(([text, wert, farbe]) => (
          <span key={text} className={farbe ?? 'text-[#1A1A1A]'}>
            <b className="tabular-nums">{zahl(wert)}</b> {text}
          </span>
        ))}
      </p>
      <p className="text-xs text-[#1A1A1A]/50">Stand {zeitpunkt(vorab.gerechnetAm)}</p>
    </div>
  );
}

/**
 * Welche Katalogdateien vorgewählt werden. Pure.
 *
 * Der Drive-Ordner gehört zum Hersteller, wenn sein Name den Profilschlüssel trägt
 * („01_uhlsport", „03_Jako"). Nur lesbare Dateien zählen.
 */
export function waehleDateienVor(
  ordner: DriveOrdner[], profil: string, mehrereDateien: boolean,
): string[] {
  const flach = (t: string) => t.toLowerCase().replace(/[^a-z0-9]/g, '');
  const passend = ordner
    .filter(o => flach(o.name).includes(flach(profil)))
    .flatMap(o => o.dateien.filter(d => d.lesbar));
  if (mehrereDateien) return passend.map(d => d.id);
  return passend.length === 1 ? [passend[0].id] : [];
}
